import React, { useEffect, useState } from 'react';
import { FiAlertTriangle } from 'react-icons/fi';
import { subscribeUnsaved, getPendingUnsaved, resolveUnsaved } from '../../lib/unsavedChanges';
import './UnsavedChangesModal.css';

// =============================================
// App-styled unsaved-changes modal (replaces native window.confirm).
// Mounted ONCE globally. Renders only while a guard request is pending.
// =============================================

const UnsavedChangesModal: React.FC = () => {
  const [, force] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => subscribeUnsaved(() => force(n => n + 1)), []);

  const pending = getPendingUnsaved();

  // Reset the saving spinner once the request clears (modal closed).
  useEffect(() => { if (!pending) setSaving(false); }, [pending]);

  // Esc = Cancel
  useEffect(() => {
    if (!pending) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) resolveUnsaved('cancel'); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [pending, saving]);

  if (!pending) return null;

  const choose = async (choice: 'save' | 'discard' | 'cancel') => {
    if (choice === 'save') setSaving(true);
    resolveUnsaved(choice);
    // The guard resolves the promise; this modal unmounts when _pending clears.
    // setSaving resets via the re-render when pending becomes null.
  };

  return (
    <div className="ucm-overlay" onMouseDown={() => !saving && resolveUnsaved('cancel')}>
      <div className="ucm-modal" role="dialog" aria-modal="true" aria-labelledby="ucm-title" onMouseDown={e => e.stopPropagation()}>
        <div className="ucm-icon"><FiAlertTriangle size={22} /></div>
        <h3 id="ucm-title" className="ucm-title">Unsaved changes</h3>
        <p className="ucm-body">{pending.message} What would you like to do?</p>
        <div className="ucm-actions">
          {pending.canSave && (
            <button className="ucm-btn ucm-save" disabled={saving} onClick={() => choose('save')}>
              {saving ? 'Saving…' : 'Save & continue'}
            </button>
          )}
          <button className="ucm-btn ucm-discard" disabled={saving} onClick={() => choose('discard')}>
            Discard changes
          </button>
          <button className="ucm-btn ucm-cancel" disabled={saving} onClick={() => choose('cancel')}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default UnsavedChangesModal;
